/**
 * GPSMap — single-point map widget for the Drop verification flow.
 *
 * Renders an OpenStreetMap iframe centred on the supplied coordinates plus a
 * fixed centre marker. No external map provider token is required. Pattern is
 * adapted from verifieddit-www/src/components/GPSMap.tsx but simplified to a
 * single point because Drop verifies one file at a time.
 */

interface GPSMapProps {
  lat: number
  lon: number
  fileName?: string
}

export function GPSMap({ lat, lon, fileName }: GPSMapProps) {
  // ~0.01° ≈ 1.1 km at the equator — a comfortable city-block-ish view.
  const span = 0.01
  const bbox = `${lon - span},${lat - span},${lon + span},${lat + span}`
  const embedUrl = `https://www.openstreetmap.org/export/embed.html?bbox=${bbox}&layer=mapnik&marker=${lat},${lon}`
  const externalUrl = `https://www.openstreetmap.org/?mlat=${lat}&mlon=${lon}&zoom=15`

  return (
    <div className="bg-[#1a1a1a] rounded-xl p-5 space-y-3">
      <div className="flex items-center justify-between">
        <h3
          className="text-[11px] font-semibold tracking-[2px] uppercase"
          style={{ color: '#aab1be', fontFamily: "'Roboto Mono', monospace" }}
        >
          Capture Location
        </h3>
        <a
          href={externalUrl}
          target="_blank"
          rel="noopener noreferrer"
          className="text-[10px] uppercase tracking-[1px] transition-colors"
          style={{ color: '#a5e3e8', fontFamily: "'Roboto Mono', monospace" }}
          aria-label="Open in OpenStreetMap"
        >
          Open in OSM ↗
        </a>
      </div>
      <div className="h-px" style={{ background: 'rgba(170,177,190,0.13)' }} />

      <div
        className="relative w-full overflow-hidden rounded-lg"
        style={{ aspectRatio: '4 / 3', background: '#0b0d10' }}
      >
        <iframe
          src={embedUrl}
          className="absolute inset-0 w-full h-full border-0"
          title={fileName ? `Capture location for ${fileName}` : 'Capture location'}
          loading="lazy"
        />
      </div>

      <div className="flex justify-between text-[12px]" style={{ fontFamily: "'Roboto Mono', monospace" }}>
        <span style={{ color: 'rgba(170,177,190,0.38)' }}>Lat, Lon</span>
        <span style={{ color: '#a5e3e8', fontWeight: 500 }}>
          {lat.toFixed(6)}, {lon.toFixed(6)}
        </span>
      </div>
    </div>
  )
}
